import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ChartFactory from "src/charts/ChartFactory";
import WidgetPlaceholder from "./WidgetPlaceholder";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";

type SpecFilterOption = number | string | { value: number | string; label: string };

type SpecFilter = {
  key: string;
  control_type: "select" | "number";
  label: string;
  default: number | string;
  options?: SpecFilterOption[];
  min?: number;
  max?: number;
};

type Props = {
  title: string;
  data: unknown;
  widgetId?: string | number;
  chartSpec?: Record<string, unknown>;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

const optVal = (o: SpecFilterOption): number | string =>
  typeof o === "object" && o !== null && "value" in o ? o.value : (o as number | string);

const optLabel = (o: SpecFilterOption): string =>
  typeof o === "object" && o !== null && "label" in o ? String(o.label) : String(o);

const accentOf = (key: string) =>
  key.startsWith("pa_") ? "#22C55E" : key.startsWith("abandon_") ? "#EF4444" : "var(--bs-primary)";

export default function TieredBarWidget({
  data: propData,
  widgetId,
  title,
  chartSpec,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: Props) {
  const [draftFilters, setDraftFilters] = useState<Record<string, number | string>>({});
  const [widgetFilters, setWidgetFilters] = useState<Record<string, number | string>>({});

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading } = useQuery({
    queryKey: ["widget", dashboardName, widgetTemplateId, appliedFilters, widgetFilters],
    queryFn: ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName!,
        String(widgetTemplateId!),
        {
          filters: appliedFilters,
          widget_filters: Object.keys(widgetFilters).length > 0 ? widgetFilters : undefined,
        },
        { signal },
      ),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const rawResponse = queryData as
    | { data?: unknown; metadata?: Record<string, unknown> }
    | undefined;

  const specFilters = useMemo<SpecFilter[]>(() => {
    const fromMeta = rawResponse?.metadata?.filters;
    if (Array.isArray(fromMeta) && fromMeta.length > 0) return fromMeta as SpecFilter[];
    return (chartSpec?.filters as SpecFilter[] | undefined) ?? [];
  }, [rawResponse, chartSpec]);

  const specDefaults = useMemo(
    () => Object.fromEntries(specFilters.map((f) => [f.key, f.default])),
    [specFilters],
  );

  const chartData = useMemo(() => {
    if (!canFetch) return propData;
    return {
      records: Array.isArray(rawResponse?.data)
        ? rawResponse.data
        : ((rawResponse?.data as any)?.records ?? []),
      tier_rules: rawResponse?.metadata?.tier_rules ?? {},
    };
  }, [canFetch, rawResponse, propData]);

  if (isLoading || !chartData) return <WidgetPlaceholder height={320} />;

  const chartId = String(widgetId ?? `tiered-bar-${title}`);

  const draftVal = (f: SpecFilter): number | string => draftFilters[f.key] ?? f.default;

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {specFilters.length > 0 && (
        <div
          className="d-flex align-items-center flex-wrap px-3 border-bottom"
          style={{
            paddingTop: 5,
            paddingBottom: 5,
            flexShrink: 0,
            gap: "4px 12px",
            background: "var(--bs-tertiary-bg, #f8fafc)",
          }}
        >
          {specFilters.map((f) => {
            const accent = accentOf(f.key);
            const val = draftVal(f);

            if (f.control_type === "select") {
              return (
                <div key={f.key} className="d-flex align-items-center gap-1">
                  <span style={{ fontSize: 11, color: "var(--bs-secondary-color)", whiteSpace: "nowrap" }}>
                    {f.label}
                  </span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto", fontSize: 11, minWidth: 56, padding: "1px 24px 1px 6px", cursor: "pointer" }}
                    value={String(val)}
                    onChange={(e) =>
                      setDraftFilters((p) => ({ ...p, [f.key]: e.target.value }))
                    }
                  >
                    {(f.options ?? []).map((opt) => {
                      const v = optVal(opt);
                      return (
                        <option key={String(v)} value={String(v)}>
                          {optLabel(opt)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              );
            }

            const numVal = Number(val);
            const min = f.min ?? 0;
            const max = f.max ?? 100;
            const pct = ((numVal - min) / (max - min)) * 100;

            return (
              <div key={f.key} className="d-flex align-items-center gap-1">
                <span style={{ fontSize: 11, color: accent, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {f.label}
                </span>
                <div style={{ width: 80 }}>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step={1}
                    value={numVal}
                    onChange={(e) =>
                      setDraftFilters((p) => ({ ...p, [f.key]: Number(e.target.value) }))
                    }
                    style={{
                      width: "100%",
                      height: 5,
                      appearance: "none",
                      WebkitAppearance: "none",
                      background: `linear-gradient(to right, ${accent} ${pct}%, var(--bs-border-color, #dee2e6) ${pct}%)`,
                      borderRadius: 4,
                      outline: "none",
                      cursor: "pointer",
                      accentColor: accent,
                    }}
                  />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: accent, minWidth: 28, textAlign: "right" }}>
                  {numVal}%
                </span>
              </div>
            );
          })}

          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={{ fontSize: 12, padding: "3px 14px", marginLeft: "auto" }}
            onClick={() => setWidgetFilters({ ...specDefaults, ...draftFilters })}
          >
            Apply
          </button>
        </div>
      )}

      <div className="flex-fill" style={{ minHeight: 0 }}>
        <ChartFactory
          chartType="tiered_bar"
          chartId={chartId}
          title={title}
          data={chartData}
          spec={chartSpec}
          fillHeight
        />
      </div>
    </div>
  );
}
